/**
 * Savings — where each year's change in TOTAL net worth came from.
 *
 * The identity, per calendar year (current year = year to date):
 *
 *   ΔNet worth = saved income + mortgage principal + retirement contributions
 *              + rental property + capital appreciation (residual)
 *   capital appreciation = property marks + market & other
 *
 * Total net worth (not FIRE) is the base on purpose: every account is in
 * scope, so money moving between our own accounts — a cash down payment
 * turning into a house, a 529 contribution, a loan payoff — nets to zero
 * inside the identity instead of needing a correction line each (the FIRE
 * version needed two and still missed the 2021 purchase).
 *
 * - saved income        income − spending under the active tag policy (the
 *                       Cash Flow page's numbers). Paycheck deposits are NET
 *                       pay, so pre-tax 401k deductions are added separately.
 * - mortgage principal  the drop in HOME mortgage balances. Those payments
 *                       are inside spending, so saved income already fell by
 *                       principal + interest while net worth fell by the
 *                       interest only; adding the principal back moves it
 *                       out of the residual. A loan with no snapshot before
 *                       the year counts from its first one.
 * - retirement          "Retirement Contributions" rows on Retirement
 *                       accounts (employee side; employer match arrives as
 *                       balance and sits in market & other).
 * - rental property     the investment property as one unit: its tag-excluded
 *                       income and expenses (rent, its mortgage payments,
 *                       insurance … — hidden from saved income by the tag
 *                       policy) PLUS its mortgage paydown. That is exactly
 *                       its contribution to net worth (rent − expenses −
 *                       mortgage interest). Its principal must NOT go in the
 *                       mortgage line: those payments never reduced saved
 *                       income, so there is nothing to add back.
 * - property marks      change in Real Estate balances over the period (the
 *                       valuation moves — a purchase itself nets against the
 *                       cash and the new mortgage).
 * - market & other      the rest of the residual: brokerage/retirement/crypto
 *                       moves, employer match, and any untracked flow.
 * - equity comp         RSU vests. No ledger source and zero going forward
 *                       (Oct 2026); a fixed 0 kept in the shape for later.
 *
 * Pure functions — the page supplies the account rules and the row
 * classifier so this file has no React or policy dependencies and compiles
 * standalone for its test.
 */
import type { Account, NetWorthData, Txn } from "./types";

/** How a row counts under the active tag policy: income/spending as the Cash
 * Flow page sees them; "excluded" = a reportable row hidden by a tag (the
 * rental's rows); null = transfers and other non-reportable rows. */
export type Flow = "income" | "spending" | "excluded" | null;

export interface SavingsRules {
  /** aggregate.ts isRentalPropertyAccount — the rental's property + mortgage. */
  isRental: (a: Account) => boolean;
  classify: (t: Txn) => Flow;
  /** Rows carrying the rental's tag (default: a tag named "Rental"). */
  isRentalRow?: (t: Txn) => boolean;
}

export interface SavingsInput {
  accounts: Account[];
  networth: NetWorthData;
  txns: Txn[];
  /** ISO date for the year-to-date row (default: today). */
  today?: string;
  /** First calendar year to report (default: the ledger display epoch). */
  fromYear?: number;
}

export interface SavingsYear {
  year: number;
  ytd: boolean;
  start: string;
  end: string;
  income: number;
  spending: number;
  savedIncome: number;
  mortgagePrincipal: number;
  retirement: number;
  equity: number;
  /** Rental: tag-excluded cash flow + its mortgage paydown. */
  rental: number;
  rentalCashFlow: number;
  rentalPrincipal: number;
  /** savedIncome + mortgagePrincipal + retirement + rental + equity: "put in". */
  saved: number;
  nwStart: number | null;
  nwEnd: number | null;
  increase: number | null;
  /** The residual; null when increase is. */
  capital: number | null;
  propertyMarks: number;
  /** capital − propertyMarks; null when capital is. */
  marketOther: number | null;
  /** capital / increase; null unless net worth GREW by a material amount —
   * at least 10% of the gross movement (|put in| + |capital|) — AND capital
   * itself was a gain. A share of a decline, or a loss's share of growth
   * carried by savings (−404% in 2022), reads as nonsense. */
  capitalShare: number | null;
}

export const NET_WORTH_KEY = "netWorth";
export const RETIREMENT_CONTRIBUTIONS = "Retirement Contributions";
export const DEFAULT_FROM_YEAR = 2014;

const round2 = (v: number) => Math.round(v * 100) / 100;
const defaultIsRentalRow = (t: Txn) => !!t.tags?.some((g) => /^rental$/i.test(g));

/** Last value of `series` on or before `date` (months are ascending). */
export function seriesAsOf(nw: NetWorthData, key: string, date: string): number | null {
  const s = nw.series[key];
  if (!s) return null;
  const m = nw.months;
  let lo = 0, hi = m.length - 1, idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (m[mid] <= date) { idx = mid; lo = mid + 1; } else hi = mid - 1;
  }
  for (let i = idx; i >= 0; i--) {
    const v = s[m[i]];
    if (v != null) return v;
  }
  return null;
}

function balanceAsOf(a: Account, date: string): number | null {
  let best: string | null = null;
  for (const k in a.balances) if (k <= date && (best === null || k > best)) best = k;
  return best === null ? null : a.balances[best];
}

function firstBalanceIn(a: Account, start: string, end: string): number | null {
  let best: string | null = null;
  for (const k in a.balances) if (k >= start && k <= end && (best === null || k < best)) best = k;
  return best === null ? null : a.balances[best];
}

/** Balance change over the period. A balance with no snapshot before the
 * period starts counting at its first one inside it. Liabilities are stored
 * negative, so a shrinking debt yields a positive change. */
function balanceChange(a: Account, start: string, end: string): number {
  const endBal = balanceAsOf(a, end);
  if (endBal == null) return 0;
  const startBal = balanceAsOf(a, start) ?? firstBalanceIn(a, start, end);
  if (startBal == null) return 0;
  return endBal - startBal;
}

function dayBefore(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function computeSavings(input: SavingsInput, rules: SavingsRules): SavingsYear[] {
  const { accounts, networth, txns } = input;
  const isRentalRow = rules.isRentalRow ?? defaultIsRentalRow;
  const today = input.today ?? new Date().toISOString().slice(0, 10);
  const thisYear = Number(today.slice(0, 4));
  const firstSeriesYear = networth.months.length ? Number(networth.months[0].slice(0, 4)) : thisYear;
  const fromYear = Math.max(input.fromYear ?? DEFAULT_FROM_YEAR, firstSeriesYear);

  const byId = new Map(accounts.map((a) => [a.id, a]));
  const homeMortgages = accounts.filter((a) => a.type === "Mortgage" && !rules.isRental(a));
  const rentalMortgages = accounts.filter((a) => a.type === "Mortgage" && rules.isRental(a));
  const properties = accounts.filter((a) => a.type === "Real Estate");

  type Acc = { income: number; spending: number; retirement: number; rentalCashFlow: number };
  const flows = new Map<number, Acc>();
  const bucket = (y: number) => {
    let b = flows.get(y);
    if (!b) { b = { income: 0, spending: 0, retirement: 0, rentalCashFlow: 0 }; flows.set(y, b); }
    return b;
  };
  for (const t of txns) {
    if (t.date > today) continue;
    const y = Number(t.date.slice(0, 4));
    if (y < fromYear) continue;
    const b = bucket(y);
    const flow = rules.classify(t);
    if (flow === "income") b.income += t.amount;
    else if (flow === "spending") b.spending -= t.amount;
    else if (flow === "excluded" && isRentalRow(t)) b.rentalCashFlow += t.amount;
    const a = byId.get(t.accountId);
    if (a && t.category === RETIREMENT_CONTRIBUTIONS && a.type === "Retirement") b.retirement += t.amount;
  }

  const out: SavingsYear[] = [];
  for (let year = fromYear; year <= thisYear; year++) {
    const start = `${year}-01-01`;
    const ytd = year === thisYear;
    const end = ytd ? today : `${year}-12-31`;
    const before = dayBefore(start);
    const b = flows.get(year) ?? { income: 0, spending: 0, retirement: 0, rentalCashFlow: 0 };
    const savedIncome = round2(b.income - b.spending);
    const mortgagePrincipal = round2(homeMortgages.reduce((s, a) => s + balanceChange(a, before, end), 0));
    const rentalPrincipal = round2(rentalMortgages.reduce((s, a) => s + balanceChange(a, before, end), 0));
    const rentalCashFlow = round2(b.rentalCashFlow);
    const rental = round2(rentalCashFlow + rentalPrincipal);
    const retirement = round2(b.retirement);
    const equity = 0;
    const saved = round2(savedIncome + mortgagePrincipal + retirement + rental + equity);
    const propertyMarks = round2(properties.reduce((s, a) => s + balanceChange(a, before, end), 0));
    const nwStart = seriesAsOf(networth, NET_WORTH_KEY, before);
    const nwEnd = seriesAsOf(networth, NET_WORTH_KEY, end);
    const increase = nwStart != null && nwEnd != null ? round2(nwEnd - nwStart) : null;
    const capital = increase == null ? null : round2(increase - saved);
    const marketOther = capital == null ? null : round2(capital - propertyMarks);
    const gross = Math.abs(saved) + Math.abs(capital ?? 0);
    const capitalShare =
      increase == null || capital == null || increase <= 0 || capital < 0 || increase < 0.1 * gross
        ? null
        : capital / increase;
    out.push({
      year, ytd, start, end,
      income: round2(b.income), spending: round2(b.spending), savedIncome,
      mortgagePrincipal, retirement, equity, rental, rentalCashFlow, rentalPrincipal, saved,
      nwStart, nwEnd, increase, capital, propertyMarks, marketOther, capitalShare,
    });
  }
  return out;
}
